import React, { useEffect, useState } from 'react';
import './AlertInfoPage.css';
import '../NewHome.css';
import AlertService from '../../../services/AlertService';
import AlertHeader from './AlertHeader';
import AlertDetails from './AlertDetails';
import { getAlertType, getAlertSeverity, formatTimestamp, getLatestStatusTimestamp } from '../../../utils/AlertUtilities';
import { useHistory, useParams } from 'react-router-dom';
import { getUserId } from '../../../utils/AuthUtilities';

import ServiceAcknowledged from './ServiceAcknowledged';

const AlertInfoPage = () => {
    const history = useHistory();
    const { id } = useParams();
    const user_id = getUserId();

    const [alert, setAlert] = useState(null);
    const [building, setBuilding] = useState(null);
    const [suite, setSuite] = useState(null);
    const [alertConfig, setAlertConfig] = useState(null);
    const [initialTimestamp, setInitialTimestamp] = useState(null);

    useEffect(() => {
        const fetchAlert = async () => {
            try {
                const alertData = await AlertService.getAlertById(id);
                console.log('Alert data with device:', alertData);
                setAlert(alertData);

                // NEW: Use dedicated endpoint for initial timestamp as requested
                const initialTimestampData = await AlertService.getInitialAlertTimestamp(id);
                console.log('Initial timestamp from dedicated endpoint:', initialTimestampData);
                setInitialTimestamp(initialTimestampData.initialTimestamp);
                
                // Fetch building data once you have the alert data
                if (alertData && alertData.buildingId) {
                    const buildingData = await AlertService.getBuildingById(alertData.buildingId);
                    setBuilding(buildingData);
                }
                if (alertData && alertData.deviceId.location && alertData.deviceId.location.suiteId) {
                    const suiteData = await AlertService.getSuiteById(alertData.deviceId.location.suiteId);
                    setSuite(suiteData);
                }

                // Fetch alert configuration to get services data
                if (alertData && alertData.alert_id) {
                    try {
                        let configData = null;
                        
                        // Strategy 1: Try exact match first
                        try {
                            configData = await AlertService.getAlertConfigByAlertId(alertData.alert_id);
                            console.log('Found config with exact match:', alertData.alert_id);
                        } catch (error) {
                            console.log('No exact match found for:', alertData.alert_id);
                        }
                        
                        // Strategy 2: For recheck alerts, strip suffixes to find base configuration
                        if (!configData) {
                            let baseAlertId = alertData.alert_id;
                            
                            // Remove "RE-CHECK " prefix if present
                            if (baseAlertId.startsWith('RE-CHECK ')) {
                                baseAlertId = baseAlertId.replace('RE-CHECK ', '');
                            }
                            
                            // Remove trailing "R" suffix if present (for recheck alerts)
                            if (baseAlertId.endsWith('R')) {
                                baseAlertId = baseAlertId.slice(0, -1);
                            }

                            console.log('Original alert_id:', alertData.alert_id);
                            console.log('Trying base alert_id:', baseAlertId);
                            
                            try {
                                configData = await AlertService.getAlertConfigByAlertId(baseAlertId);
                                console.log('Found config with base match:', baseAlertId);
                            } catch (error) {
                                console.log('No base match found for:', baseAlertId);
                            }
                        }

                        if (configData) {
                            setAlertConfig(configData);
                        } else {
                            console.log('No configuration found for alert, using defaults');
                            setAlertConfig({
                                services: {
                                    initialChecks: ["No initial checks configured"],
                                    suggestedServices: ["No suggested services configured"]
                                }
                            });
                        }
                    } catch (error) {
                        console.error('Failed to fetch alert configuration:', error);
                        setAlertConfig({
                            services: {
                                initialChecks: ["No initial checks configured"],
                                suggestedServices: ["No suggested services configured"]
                            }
                        });
                    }
                }
            } catch (error) {
                console.error('Failed to fetch alert:', error);
            }
        };

        fetchAlert();
    }, [id]);

    if (!alert) {
        return <div className='loader-container'>
                    <div className="loader"></div>
                </div>;
    }

    const alertType = getAlertType(alert.alert_id);
    const alertSeverity = getAlertSeverity(alert.alert_id);
    
    // Use new endpoint data for initial date, statusHistory for last updated
    const initialDate = initialTimestamp ? formatTimestamp(initialTimestamp) : null;
    const lastUpdatedDate = alert.statusHistory && alert.statusHistory.length > 0 
        ? formatTimestamp(getLatestStatusTimestamp(alert.statusHistory))
        : null;

    const handleViewAlertData = () => {
        history.push(`/alert/${id}/details`, {
            alertType,
            alertId: alert.alert_id,
            id: alert._id,
            alertSeverity,
            date: initialDate,
            alertName: alert.alert_name,
            metadata: alert.metadata,
            device: alert.deviceId,
            address: null,
            unit: null,
            building: null,
            suite: null,
            user: null,
            assignedPersonnel: null,
            assign: null,
            status: null,
            serviceId: null
        });
    };
 
    const handleAssignAlert = () => {
        history.push(`/alert/${id}/details`, {
            alertType,
            alertId: alert.alert_id,
            id: alert._id,
            alertSeverity,
            date: null,
            alertName: alert.alert_name,
            metadata: alert.metadata,
            device: null,
            address: null,
            unit: null,
            building: building,
            suite: suite,
            user_id: user_id,
            assignedPersonnel: alert.assignedPersonnel || [],
            assign: 'assign',
            status: null,
            serviceId: null
        });
    };

    const handleAlertStatus = () => {
        history.push(`/alert/${id}/details`, {
            alertType,
            alertId: alert.alert_id,
            id: alert._id,
            alertSeverity,
            date: null,
            alertName: alert.alert_name,
            metadata: alert.metadata,
            device: null,
            address: null,
            unit: null,
            building: building,
            suite: suite,
            user_id: user_id,
            assignedPersonnel: alert.assignedPersonnel || [],
            assign: null,
            status: alert.status,
            serviceId: alert.serviceRecordId
        });
    };
 
    // Get services data from alert configuration or use defaults
    const getServicesData = () => {
        if (alertConfig && alertConfig.services) {
            return {
                initialChecks: alertConfig.services.initialChecks || ["No initial checks configured"],
                suggestedServices: alertConfig.services.suggestedServices || ["No suggested services configured"]
            };
        }
        
        // Fallback to default values while loading
        return {
            initialChecks: ["Loading initial checks..."],
            suggestedServices: ["Loading suggested services..."]
        };
    };

    const servicesData = getServicesData();

    return (
        <div>
        <div className="AlertPage">
            <div className="alerts-section">
                <AlertHeader 
                        alertType={alertType} 
                        alertId={alert.alert_id} 
                />
                <div className='info-page' style={{width: '100%'}}>
                    <div className='content-container'
                         style={alert.status === 'Investigating' ? { height: '510px' } : {}}>
                        <AlertDetails
                            alertType={alertType}
                            alertId={alert._id}
                            alertSeverity={alertSeverity}
                            date={initialDate}
                            lastUpdated={lastUpdatedDate}
                            description={alert.alert_desc}
                            address={building ? building.address : 'Loading...'}
                            unit={Array.isArray(alert.metadata?.data) 
                                ? 'See Alert Data'
                                : alert.metadata.location ?? "No data"} // Single sensor
                            alertStatus={alert.status}
                            alertName={alert.alert_name}
                            initialChecks={servicesData.initialChecks}
                            suggestions={servicesData.suggestedServices}
                            deviceData={alert.deviceId} // Pass device data for fallback
                            alertAssetId={alert.asset} // NEW: Pass alert asset ID directly
                        />
                    </div>
                    <div className="button-container">
                        <button className="alert-info-button" style={{backgroundColor: "#d3d3d36b"}} 
                            onClick={handleViewAlertData}>View Alert Data</button>

                        {/* Status dependant buttons */ }
                        {alert.status === 'Investigating' && (
                            <button className="alert-info-button" onClick={handleAssignAlert}>Reassign Alert</button>
                        )}
                        {(alert.status === 'New' || alert.status === 'Active' || alert.status === 'Assigned') ? (
                            <ServiceAcknowledged
                                newStatus='Acknowledged'
                                alertId={alert._id}
                                action='Acknowledge'
                                buildingId={building}
                            />
                        ) : (alert.status === 'Acknowledged') ? (
                            <ServiceAcknowledged
                                newStatus='Investigating'
                                alertId={alert._id}
                                action='Investigate'
                                buildingId={building}
                            />
                        ) : (
                            <button className="alert-info-button" onClick={handleAlertStatus}>Update Status</button>
                        )}
                    </div>
                </div>
            </div>
        </div>
        </div>
    );
};

export default AlertInfoPage;